import { Document, Image, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { convertDecimals, roundTwoDecimal } from '../calculation/util';
import { EUnitOfMeasureImperialLabel, EUnitOfMeasureMetricLabel } from '../conversion/enum';
import drainHorizontalLegend from '../design/engine/image/drainHorizontalLegend.png';
import drainPipeLegend from '../design/engine/image/drainPipeLegend.png';
import drainageLayingPlanLegend from '../design/engine/image/drainageLayingPlanLegend.png';
import fsDefaultLegend from '../design/engine/image/fsDefaultLegend.png';
import fsSeismicLegend from '../design/engine/image/fsSeismicLegend.png';
import fsSeismicMinLegend from '../design/engine/image/fsSeismicMinLegend.png';
import fsStaticLegend from '../design/engine/image/fsStaticLegend.png';
import fsStaticMinLegend from '../design/engine/image/fsStaticMinLegend.png';
import initialWaterLegend from '../design/engine/image/initialWaterLegend.png';
import postFsSeismicLegend from '../design/engine/image/postFsSeismicLegend.png';
import postFsStaticLegend from '../design/engine/image/postFsStaticLegend.png';
import preFsSeismicLegend from '../design/engine/image/preFsSeismicLegend.png';
import preFsStaticLegend from '../design/engine/image/preFsStaticLegend.png';
import overloadLegend from '../design/engine/image/overloadLegend.png';
import {
    getDrainageCapacityTable,
    getDrainageSystemTable,
    getDrainingDrainPipesTable,
    getGeneralPressuresInTheSoilTable,
    getInterventionSlopeValidationTable,
    getLongTermPressuresInTheSoilTable,
    getMetricCalculationAmountTable,
    getMetricCalculationCommonTable,
    getMetricCalculationExcavationTable,
    getMetricCalculationPriceInputTable,
    getMetricCalculationPriceOutputTable,
    getMetricCalculationTotalVolumesTable,
    getShortTermPressuresInTheSoilTable,
    getTransitionalRegimeTable,
    getWheelBaseTable,
} from '../design/result/util';
import { EDrainageTrenchType, ERole, EStableSlopeValidationType, EUnitOfMeasure } from '../shared/enum';
import { getDischargeTable, getDrainageTrenchTable, getLayersInSeriesTable, getLoadTable, getSlopeTable, getSoilLayersTable, getSoilTable } from '../shared/input/util';
import variable from '../shared/style/variable';
import { redux } from '../store';
import Legend, { ILegend } from './component/Legend';
import PageLayout from './component/PageLayout';
import Table from './component/Table';
import styles from './style';

const drain = 'drain';
const drainageLayingPlan = 'drainageLayingPlan';
const drainPipe = 'drainPipe';
const initialGroudwater = 'initialGroudwater';
const preFsStatic = 'preFsStatic';
const postFsStatic = 'postFsStatic';
const preFsSeismic = 'preFsSeismic';
const postFsSeismic = 'postFsSeismic';

const distributedOverloadLegend = 'distributedOverloadLegend';

export const Pdf = () => {
    const user = redux.getState().user;
    const project = redux.getState().project;
    const result = redux.getState().result;
    const price = redux.getState().price;
    const image = redux.getState().image;
    const util = redux.getState().util;

    const { t } = useTranslation();

    let output = convertDecimals(result.data!);

    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;

    //input
    let slopeTable = getSlopeTable(project.data?.slope!);
    let loadTable = getLoadTable(project.data?.load!);
    let dischargeTable = getDischargeTable(project.data?.discharge!, project.data?.discharge?.dischargeType!);
    let drainageTrenchTable = getDrainageTrenchTable(project.data?.drainageTrenches!);
    let soilTable = getSoilTable(project.data?.soilInputData?.soils![0]!);
    let soilLayersTable = getSoilLayersTable(project.data?.soilInputData?.soils!);
    let layersInSeriesTable = getLayersInSeriesTable(project.data?.soilInputData!);

    //output
    let drainageCapacityTable = getDrainageCapacityTable(result.data!.phaseTwoOutputCalculation.drainingCapacity); //different decimal numbers
    let drainingDrainPipesTable = getDrainingDrainPipesTable(output.phaseTwoOutputCalculation.drainingDrainPipes);
    let wheelbaseTable = getWheelBaseTable(output.phaseTwoOutputCalculation.wheelbase, true);
    let preInterventionSlopeValidationTable = getInterventionSlopeValidationTable(output.phaseOneOutputCalculation, EStableSlopeValidationType.Pre);
    let postInterventionSlopeValidationTable = getInterventionSlopeValidationTable(
        output.phaseTwoOutputCalculation.fsTablesWithResults.evaluationOfSlopeStabilityPostIntervention,
        EStableSlopeValidationType.Post,
    );
    let transitionalRegimeTable = getTransitionalRegimeTable(output.phaseTwoOutputCalculation.transitionalRegime);
    let drainageSystemTable = getDrainageSystemTable(output.phaseTwoOutputCalculation.transitionalRegime.drainageSystem, output.phaseTwoOutputCalculation.transitionalRegime);
    let generalPressuresInTheSoilTable = getGeneralPressuresInTheSoilTable(output.phaseTwoOutputCalculation.calculationOfPressuresInTheSoil);
    let longTermPressuresInTheSoilTable = getLongTermPressuresInTheSoilTable(output.phaseTwoOutputCalculation.calculationOfPressuresInTheSoil.longTermDrain);
    let shortTermPressuresInTheSoilTable = getShortTermPressuresInTheSoilTable(output.phaseTwoOutputCalculation.calculationOfPressuresInTheSoil.shortTermDrain);

    //metricCalculation
    let metricCalculationAmountOutput = getMetricCalculationAmountTable(output.phaseTwoOutputCalculation.metricCalculation);
    let metricCalculationCommonTable = getMetricCalculationCommonTable(output.phaseTwoOutputCalculation.metricCalculation);
    let excavationCalculationOutput = getMetricCalculationExcavationTable(output.phaseTwoOutputCalculation.metricCalculation.excavationCalculation);
    let totalVolumesArea = getMetricCalculationTotalVolumesTable(output.phaseTwoOutputCalculation.metricCalculation.totalVolumes);
    let metricCalculationPriceInput = getMetricCalculationPriceInputTable(price.data!, output.phaseTwoOutputCalculation.metricCalculation);
    let metricCalculationPriceOutput = getMetricCalculationPriceOutputTable(price.data!, output.phaseTwoOutputCalculation.metricCalculation);

    let legendPreliminaryCalculation = [
        { name: 'fsStatic', logo: fsStaticLegend },
        project.data?.load?.hasSeismicLoads ? { name: 'fsSeismic', logo: fsSeismicLegend } : undefined,
        { name: 'fsDefault', logo: fsDefaultLegend, value: 1, height: 3 },
        { name: 'minFsStaticConditionsWithValue', logo: fsStaticMinLegend, value: roundTwoDecimal(project.data?.load?.minFsStaticConditions!), height: 3 },
        project.data?.load?.hasSeismicLoads
            ? { name: 'minFsSeismicConditionsWithValue', logo: fsSeismicMinLegend, value: roundTwoDecimal(project.data?.load?.minFsSeismicConditions!), height: 3 }
            : undefined,
        {
            name: 'initialGroudwaterWithValue',
            logo: initialWaterLegend,
            value: `${project.data?.slope?.initialWaterTableDepth} ${
                project.data?.projectInfo.unitOfMeasure == EUnitOfMeasure.Metric ? EUnitOfMeasureMetricLabel.meter : EUnitOfMeasureImperialLabel.foot
            }`,
            height: 3,
        },
    ];

    let legendTransveralSection: ILegend[] = [
        { name: initialGroudwater, logo: initialWaterLegend, height: 3 },
        { name: drainageLayingPlan, logo: drainageLayingPlanLegend, height: 3 },
        { name: drain, logo: drainHorizontalLegend },
    ];

    if (project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product?.hasPipe) {
        legendTransveralSection.push({ name: drainPipe, logo: drainPipeLegend, width: 5, height: 5 });
    }

    let legendLongitudinalStaticSection: ILegend[] = [
        { name: initialGroudwater, logo: initialWaterLegend, height: 3 },
        { name: drainageLayingPlan, logo: drainageLayingPlanLegend, height: 3 },
        { name: drain, logo: drainHorizontalLegend },
        { name: 'minFsStaticConditionsWithValue', logo: fsStaticMinLegend, value: roundTwoDecimal(project.data?.load?.minFsStaticConditions!), height: 3 },
        { name: preFsStatic, logo: preFsStaticLegend, height: 3 },
        { name: postFsStatic, logo: postFsStaticLegend, height: 3 },
    ];

    let legendLongitudinalSeismicSection: ILegend[] = [
        { name: initialGroudwater, logo: initialWaterLegend, height: 3 },
        { name: drainageLayingPlan, logo: drainageLayingPlanLegend, height: 3 },
        { name: drain, logo: drainHorizontalLegend },
        { name: 'minFsSeismicConditionsWithValue', logo: fsSeismicMinLegend, value: roundTwoDecimal(project.data?.load?.minFsSeismicConditions!), height: 3 },
        { name: preFsSeismic, logo: preFsSeismicLegend, height: 3 },
        { name: postFsSeismic, logo: postFsSeismicLegend, height: 3 },
    ];

    if (project.data?.load?.distributedOverload! > 0) {
        legendTransveralSection.push({ name: distributedOverloadLegend, logo: overloadLegend, width: 10, height: 15 });
        legendLongitudinalStaticSection.push({ name: distributedOverloadLegend, logo: overloadLegend, width: 10, height: 15 });
        legendLongitudinalSeismicSection.push({ name: distributedOverloadLegend, logo: overloadLegend, width: 10, height: 15 });
    }

    return (
        <Document>
            <PageLayout>
                <>
                    <View style={styles.section}>
                        <View style={{ flexDirection: 'row' }}>
                            <View style={{ width: '75%', flexDirection: 'column' }}>
                                <Text style={{ ...styles.h1, paddingBottom: 10 }}>{t('projectInfo')}</Text>
                                <View style={{ flexDirection: 'row', padding: '5 0' }}>
                                    <View style={{ width: '50%', flexDirection: 'column' }}>
                                        <Text style={styles.h2}>{t('name')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.title}</Text>
                                    </View>
                                    <View style={{ width: '50%', flexDirection: 'column' }}>
                                        <Text style={styles.h2}>{t('companyName')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.companyName}</Text>
                                    </View>
                                </View>
                                <View style={{ flexDirection: 'row', padding: '5 0' }}>
                                    <View style={{ width: '50%', flexDirection: 'column' }}>
                                        <Text style={styles.h2}>{t('client')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.client}</Text>
                                    </View>
                                    <View style={{ width: '50%', flexDirection: 'column' }}>
                                        <Text style={styles.h2}>{t('projectNumber')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.projectNumber}</Text>
                                    </View>
                                </View>
                                <View style={{ flexDirection: 'row', padding: '5 0' }}>
                                    <View style={{ width: '50%', flexDirection: 'column' }}>
                                        <Text style={styles.h2}>{t('country')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.country}</Text>
                                    </View>
                                    <View style={{ width: '50%', flexDirection: 'column' }}>
                                        <Text style={styles.h2}>{t('city')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.city}</Text>
                                    </View>
                                </View>
                                <View style={{ flexDirection: 'row', padding: '5 0' }}>
                                    <View style={{ width: '50%', flexDirection: 'column' }}>
                                        <Text style={styles.h2}>{t('latitude')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.latitude}</Text>
                                    </View>
                                    <View style={{ width: '50%', flexDirection: 'column' }}>
                                        <Text style={styles.h2}>{t('longitude')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.longitude}</Text>
                                    </View>
                                </View>
                                {project.data?.projectInfo?.notes && (
                                    <View style={{ padding: '5 0' }}>
                                        <Text style={styles.h2}>{t('notes')}</Text>
                                        <Text style={styles.h3}>{project.data?.projectInfo?.notes}</Text>
                                    </View>
                                )}
                            </View>
                            <View style={{ width: '40%', border: `1px solid ${variable.turquoise_400}` }}>
                                <Image src={image.data!.map!} />
                            </View>
                        </View>
                    </View>
                    <View style={styles.section}>
                        <Text style={{ ...styles.h1, paddingBottom: 10 }}>{t('3DEngine')}</Text>
                        <View style={{ height: 250, backgroundColor: variable.turquoise_100 }}>
                            <Image style={{ margin: 'auto', height: 250 }} src={image.data!.generalView} />
                        </View>
                    </View>
                    <View style={styles.section}>
                        <Text style={{ ...styles.h1, paddingBottom: 10 }}>{t('input')}</Text>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                            <View style={{ width: '48%' }}>
                                <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('slope')}</Text>
                                <Table data={slopeTable} />
                            </View>
                            <View style={{ width: '48%' }}>
                                <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('loads')}</Text>
                                <Table data={loadTable} />
                            </View>
                        </View>
                    </View>
                </>
            </PageLayout>
            <PageLayout>
                <>
                    <View style={styles.section}>
                        <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('discharge')}</Text>
                        <Table data={dischargeTable} />
                    </View>
                    <View style={styles.section}>
                        <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('drains')}</Text>
                        <Table data={drainageTrenchTable} />
                    </View>
                </>
            </PageLayout>
            <PageLayout>
                <>
                    <View style={styles.section}>
                        <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('soils')}</Text>
                        <Table data={soilTable} />
                    </View>
                    <View style={styles.section}>
                        <Table data={soilLayersTable} />
                    </View>
                    {project.data?.soilInputData?.soils?.length! > 1 && (
                        <View style={styles.section}>
                            <Table data={layersInSeriesTable} />
                        </View>
                    )}
                </>
            </PageLayout>
            <PageLayout>
                <>
                    <View style={styles.section}>
                        <Text style={{ ...styles.h1, paddingBottom: 5 }}>{t('preliminaryCalculation')}</Text>
                        <View style={{ height: 700, backgroundColor: variable.turquoise_100 }}>
                            <Legend data={legendPreliminaryCalculation} />
                            <Image style={{ margin: 'auto', height: 280 }} src={image.data!.preliminaryCalculation} />
                        </View>
                    </View>
                </>
            </PageLayout>
            <PageLayout>
                <View style={styles.section}>
                    <Text style={{ ...styles.h1, paddingBottom: 5 }}>
                        {t('transversalView')} - {t('primaryDrain')}
                    </Text>
                    <View style={{ height: 400, backgroundColor: variable.turquoise_100 }}>
                        <Legend data={legendTransveralSection} />
                        <Image style={{ margin: 'auto', height: 300 }} src={image.data!.transversalSectionPrimaryDrainageTrench} />
                    </View>
                </View>
                {hasSecondaryDrainageTrench && (
                    <View style={styles.section}>
                        <Text style={{ ...styles.h1, paddingBottom: 5 }}>
                            {t('transversalView')} - {t('secondaryDrain')}
                        </Text>
                        <View style={{ height: 400, backgroundColor: variable.turquoise_100 }}>
                            <Legend data={legendTransveralSection} />
                            <Image style={{ margin: 'auto', height: 300 }} src={image.data!.transversalSectionSecondaryDrainageTrench} />
                        </View>
                    </View>
                )}
            </PageLayout>
            <PageLayout>
                <>
                    <View style={styles.section}>
                        <Text style={{ ...styles.h1, paddingBottom: 5 }}>{t('longitudinalStaticView')}</Text>
                        <View style={{ height: 300, backgroundColor: variable.turquoise_100 }}>
                            <Legend data={legendLongitudinalStaticSection} />
                            <Image style={{ margin: 'auto', height: 300 }} src={image.data!.longitudinalStaticSection} />
                        </View>
                    </View>
                    {project.data?.load?.hasSeismicLoads && (
                        <View style={styles.section}>
                            <Text style={{ ...styles.h1, paddingBottom: 5 }}>{t('longitudinalSeismicView')}</Text>
                            <View style={{ height: 300, backgroundColor: variable.turquoise_100 }}>
                                <Legend data={legendLongitudinalSeismicSection} />
                                <Image style={{ margin: 'auto', height: 300 }} src={image.data!.longitudinalSeismicSection} />
                            </View>
                        </View>
                    )}
                </>
            </PageLayout>
            {(util.data?.stableSlopeAssessmentTable ||
                util.data?.drainageCapacityTable ||
                (drainingDrainPipesTable && util.data?.drainingDrainPipesCalculationTable) ||
                util.data?.wheelbaseOutputTable) && (
                <PageLayout>
                    <>
                        <View style={styles.section}>
                            <Text style={{ ...styles.h1, paddingBottom: 10 }}>{t('output')}</Text>
                        </View>
                        {util.data?.stableSlopeAssessmentTable && (
                            <View style={styles.section}>
                                <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('stableSlopeAssessment')}</Text>
                                <Table data={preInterventionSlopeValidationTable} />
                                <Table data={postInterventionSlopeValidationTable} />
                            </View>
                        )}
                        {util.data?.drainageCapacityTable && (
                            <View style={styles.section}>
                                <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('drainageCapacity')}</Text>
                                <Table data={drainageCapacityTable} />
                            </View>
                        )}
                        {drainingDrainPipesTable && util.data?.drainingDrainPipesCalculationTable && (
                            <View style={styles.section}>
                                <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('drainingDrainPipesCalculation')}</Text>
                                <Table data={drainingDrainPipesTable} />
                            </View>
                        )}
                        {util.data?.wheelbaseOutputTable && (
                            <View style={styles.section}>
                                <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('wheelbaseOutput')}</Text>
                                <Table data={wheelbaseTable} />
                            </View>
                        )}
                    </>
                </PageLayout>
            )}
            {(util.data?.transitionalRegimeTable || util.data?.pressuresInTheSoilCalculationTable) && (
                <PageLayout>
                    {util.data?.transitionalRegimeTable && (
                        <View style={styles.section}>
                            <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('transitionalRegime')}</Text>
                            <Table data={transitionalRegimeTable} />
                            <Table data={drainageSystemTable} />
                        </View>
                    )}
                    {util.data?.pressuresInTheSoilCalculationTable && (
                        <View style={styles.section}>
                            <Text style={{ ...styles.h3, paddingBottom: 5 }}>{t('pressuresInTheSoilCalculation')}</Text>
                            <Table data={generalPressuresInTheSoilTable} />
                            <Table data={longTermPressuresInTheSoilTable} />
                            <Table data={shortTermPressuresInTheSoilTable} />
                        </View>
                    )}
                </PageLayout>
            )}
            {(util.data?.metricCalculationTable || (user.data?.role != ERole.Standard && util.data?.priceTable)) && (
                <PageLayout>
                    <>
                        {util.data?.metricCalculationTable && (
                            <View style={styles.section}>
                                <Text style={{ ...styles.h1, paddingBottom: 10 }}>{t('metricCalculation')}</Text>
                                <Table data={metricCalculationAmountOutput} />
                                <Table data={metricCalculationCommonTable} />
                                <Table data={excavationCalculationOutput} />
                                <Table data={totalVolumesArea} />
                            </View>
                        )}
                        {user.data?.role != ERole.Standard && util.data?.priceTable && (
                            <View style={styles.section}>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                    <View style={{ width: '48%' }}>
                                        <Table data={metricCalculationPriceInput} />
                                    </View>
                                    <View style={{ width: '48%' }}>
                                        <Table data={metricCalculationPriceOutput} />
                                    </View>
                                </View>
                            </View>
                        )}
                    </>
                </PageLayout>
            )}
        </Document>
    );
};

export default Pdf;
